import { decode, encode } from 'cbor-x'
import AES from 'crypto-js/aes'
import Utf8 from 'crypto-js/enc-utf8'
import dayjs from 'dayjs'
import slimStorage from 'localstorage-slim'

import { getFingerprint } from './fingerprint'

export class SecureLocalStorage implements Storage {
  private readonly _key = 'PLM'
  private readonly _localItems: Record<string, string | object | number | boolean | null> = {}
  private readonly timezone = dayjs?.tz?.guess()
  private readonly storage = slimStorage

  constructor(params: { encrypt: boolean, ttl?: number | null } = { encrypt: true }) {
    this.storage.config.ttl = params.ttl
    this.storage.config.storage = localStorage
    this.storage.config.encrypt = params.encrypt
    // this.storage.config.decrypt = true;
    this.storage.config.secret
      = getFingerprint('localStorage', this.timezone)
        + (import.meta.env.VITE_LOCAL_STORAGE_HASH_KEY || '')
    this.storage.config.storage = localStorage

    this.storage.config.encrypter = this.encyrpter

    this.storage.config.decrypter = this.decrypter

    this._localItems = this.getAllLocalStorageItems()
  }

  [name: string]: any;

  length: number = localStorage.length

  key(index: number): string | null {
    return localStorage.key(index)
  }

  private cborxDecode(data: string) {
    const dataArray = data.split(',').map(value => Number(value))
    const byteArray = new Uint8Array(dataArray)
    const decodedData = decode(byteArray)

    return decodedData
  }

  private encyrpter(data: unknown, secret: unknown) {
    return AES.encrypt(`${data}` as string, secret as string).toString()
  }

  private decrypter(encryptedString: any, secret: any): any {
    try {
      const bytes = AES.decrypt(encryptedString as string, secret as string)
      const data = bytes.toString(Utf8) || null

      if (data) {
        return this.cborxDecode(data)
      }

      return data
    }
    catch (err) {
      return null
    }
  }

  private getKey(key: string) {
    return `${this._key}.L.${key}`
  }

  private getAllLocalStorageItems() {
    const localStorageItems: Record<string, string | object | number | boolean | null> = {}

    if (typeof window != 'undefined') {
      for (const [key, value] of Object.entries(localStorage)) {
        if (key.startsWith(this.getKey(''))) {
          if (this.storage.config.encrypt) {
            localStorageItems[key] = this.decrypter(JSON.parse(value), this.storage.config.secret)
          }
          else {
            localStorageItems[key] = this.cborxDecode(JSON.parse(value))
          }
        }
      }
    }

    return localStorageItems
  }

  clear(): void {
    this.storage.clear()
  }

  getItem(key: string): any {
    const parsedKey = this.getKey(key)

    if (this.storage.config.encrypt) {
      return (
        this._localItems[parsedKey]
        || this.storage.get(parsedKey, {
          decrypt: this.storage.config.encrypt,
        })
      )
    }
    else {
      return this._localItems[parsedKey] || this.cborxDecode(this.storage.get(parsedKey) as any)
    }
  }

  removeItem(key: string): void {
    const parsedKey = this.getKey(key)

    if (this._localItems[parsedKey] !== undefined) {
      delete this._localItems[parsedKey]
    }

    this.storage.remove(parsedKey)
  }

  setItem(key: string, value: any): void {
    let parsedKey = this.getKey(key)

    if (key != null) {
      this._localItems[parsedKey] = value
    }

    this.storage.set(parsedKey, encode(value).toString('hex'))
  }
}
